/**
 * ayni-store-availability.js
 * Handles store pickup availability badge and drawer on the PDP.
 *
 * Bugs fixed:
 *  1. Drawer-Selektor: war getElementById('StoreAvailabilityDrawer'),
 *     aber HTML hat id="store-availability-drawer" → class-basierter Selektor
 *  2. Button-Attribut: war [data-store-availability-toggle],
 *     HTML hat data-store-availability-trigger → korrigiert
 *  3. Doppelter Drawer: Snippet wird im Cart-Drawer UND im PDP gerendert.
 *     Fix: connectedCallback bricht ab wenn das Element im Cart-Drawer ist;
 *     der Drawer-Selektor sucht explizit den Drawer außerhalb des Cart-Drawers.
 *  4. Section Rendering API URL: war /variants/{id}?section_id=...
 *     → liefert keinen product-Kontext, Metafields werden nicht aufgelöst.
 *     Jetzt: /products/{handle}?variant={id}&section_id=...
 *  5. Variant-Change-Fallback: popstate + pushState-Intercept für Blum
 *  6. JSON-Felder: war loc.address1/address2 → jetzt loc.address (wie im Liquid)
 */

class StoreAvailabilityRoot extends HTMLElement {
  connectedCallback() {
    // Skip init if this element is inside a cart drawer
    if (this.closest('cart-drawer, sht-cart-drwr, [data-cart-drawer]')) return;

    // Find the correct drawer – the one NOT inside a cart drawer
    const allDrawers = document.querySelectorAll('#store-availability-drawer');
    this.drawer = Array.from(allDrawers).find(
      d => !d.closest('cart-drawer, sht-cart-drwr, [data-cart-drawer]')
    ) || allDrawers[0];

    if (!this.drawer) return;

    this.overlay   = this.drawer.querySelector('.store-availability-drawer__overlay');
    this.closeBtn  = this.drawer.querySelector('.store-availability-drawer__close');
    this.contentEl = this.drawer.querySelector('.store-availability-drawer__content');

    // Events
    this.querySelector('[data-store-availability-trigger]')
      ?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click',  () => this.close());
    this.overlay?.addEventListener('click',   () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // Variant change – Blum / Dawn fire 'variant:change'
    document.addEventListener('variant:change', (e) => this.onVariantChange(e.detail?.variant));

    // Fallback: URL-param observer for themes using History API
    this._productHandle = this.dataset.productHandle;
    this._lastVariantId = this.dataset.variantId;

    // Fallback 1: popstate (Back/Forward navigation)
    this._onPopState = () => this._checkUrlVariant();
    window.addEventListener('popstate', this._onPopState);

    // Fallback 2: intercept history.pushState + replaceState
    // Blum mobile sometimes uses replaceState instead of pushState
    if (!window._ayniHistoryPatched) {
      window._ayniHistoryPatched = true;
      const _dispatch = () => window.dispatchEvent(new Event('ayni:urlchange'));
      const origPush    = history.pushState.bind(history);
      const origReplace = history.replaceState.bind(history);
      history.pushState    = function (...a) { origPush(...a);    _dispatch(); };
      history.replaceState = function (...a) { origReplace(...a); _dispatch(); };
    }
    this._onUrlChange = () => this._checkUrlVariant();
    window.addEventListener('ayni:urlchange', this._onUrlChange);

    // Fallback 3: native change events on variant selects/radios
    // Fires reliably on mobile when the user taps a swatch or dropdown
    this._onFormChange = () => setTimeout(() => this._checkUrlVariant(), 100);
    document
      .querySelector('form[action*="/cart/add"], form[action="/cart/add"]')
      ?.addEventListener('change', this._onFormChange);

    // Fallback 4: MutationObserver with subtree (catch any DOM-driven update)
    this._observer = new MutationObserver(
      this._debounce(() => this._checkUrlVariant(), 250)
    );
    this._observer.observe(document.getElementById('MainContent') || document.body, {
      childList: true,
      subtree: true,
    });

    // Initial drawer populate
    this._populateDrawer(this._readJson());
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    window.removeEventListener('popstate',      this._onPopState);
    window.removeEventListener('ayni:urlchange', this._onUrlChange);
    document
      .querySelector('form[action*="/cart/add"], form[action="/cart/add"]')
      ?.removeEventListener('change', this._onFormChange);
  }

  _debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  _checkUrlVariant() {
    const vid = new URLSearchParams(window.location.search).get('variant');
    if (vid && vid !== this._lastVariantId) {
      this._lastVariantId = vid;
      this.updateAvailability(vid);
    }
  }

  // ── Drawer open / close ────────────────────────────────────────────────────

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.closeBtn?.focus();
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
  }

  // ── Variant change ─────────────────────────────────────────────────────────

  onVariantChange(variant) {
    // variant.id may be a numeric ID (number or string), not a GID
    const vid = variant?.id ?? variant?.variantId;
    if (!vid) return;
    this._lastVariantId = String(vid);
    this.updateAvailability(String(vid));
  }

  async updateAvailability(variantId) {
    if (!variantId) return;
    // /products/{handle}?variant={id}&section_id=... → correct product context for metafield resolution
    const handle = this._productHandle;
    const url = handle
      ? `${window.Shopify.routes.root}products/${handle}?variant=${variantId}&section_id=ayni-store-availability`
      : `${window.Shopify.routes.root}variants/${variantId}?section_id=ayni-store-availability`;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Replace badge
      const newBadge = doc.querySelector('.store-availability__badge');
      const curBadge = this.querySelector('.store-availability__badge');
      if (newBadge && curBadge) {
        curBadge.replaceWith(newBadge.cloneNode(true));
        this.querySelector('[data-store-availability-trigger]')
          ?.addEventListener('click', () => this.open());
      }

      // Replace JSON payload
      const newJson = doc.querySelector('[data-store-availability-json]');
      const curJson = this.querySelector('[data-store-availability-json]');
      if (newJson && curJson) curJson.textContent = newJson.textContent;

      this._populateDrawer(this._readJson());
    } catch (e) {
      console.warn('[StoreAvailability] Fetch error:', e);
    }
  }

  // ── Drawer content ─────────────────────────────────────────────────────────

  _readJson() {
    try {
      const el = this.querySelector('[data-store-availability-json]');
      return el ? JSON.parse(el.textContent) : null;
    } catch {
      return null;
    }
  }

  _populateDrawer(data) {
    if (!data || !this.contentEl) return;
    this.contentEl.innerHTML = '';

    if (!data.locations?.length) {
      this.contentEl.innerHTML =
        '<p class="store-availability-drawer__empty">Derzeit kein Standort verfügbar.</p>';
      return;
    }

    // Product header: thumbnail + title/variant
    const productHeader = document.createElement('div');
    productHeader.className = 'store-availability-drawer__product-header';

    if (data.productImage) {
      const img = document.createElement('img');
      img.src = data.productImage;
      img.alt = data.productTitle || '';
      img.className = 'store-availability-drawer__product-image';
      img.width = 64;
      img.height = 64;
      img.loading = 'lazy';
      productHeader.appendChild(img);
    }

    const productInfo = document.createElement('div');
    productInfo.className = 'store-availability-drawer__product-info';
    productInfo.innerHTML =
      `<p class="store-availability-drawer__product-title">${this._esc(data.productTitle || '')}</p>` +
      (data.variantTitle && data.variantTitle !== 'Default Title'
        ? `<p class="store-availability-drawer__product-variant">${this._esc(data.variantTitle)}</p>`
        : '');
    productHeader.appendChild(productInfo);
    this.contentEl.appendChild(productHeader);

    const ul = document.createElement('ul');
    ul.className = 'store-availability-drawer__list';

    for (const loc of data.locations) {
      const li = document.createElement('li');
      li.className = 'store-availability-drawer__item';
      li.innerHTML = `
        <div class="store-availability-drawer__location-header">
          <span class="store-availability-drawer__check" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
          </span>
          <strong class="store-availability-drawer__name">${this._esc(loc.name)}</strong>
        </div>
        <address class="store-availability-drawer__address">
          ${loc.address ? `${this._esc(loc.address)}<br>` : ''}
          ${(loc.zip || loc.city) ? `${this._esc(loc.zip || '')} ${this._esc(loc.city || '')}<br>` : ''}
          ${loc.phone ? `<a href="tel:${this._esc(loc.phone)}">${this._esc(loc.phone)}</a>` : ''}
        </address>`;
      ul.appendChild(li);
    }

    this.contentEl.appendChild(ul);
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('store-availability-root')) {
  customElements.define('store-availability-root', StoreAvailabilityRoot);
}